import { Link } from "react-router-dom";
import { Twitter, Instagram, AtSign, Globe, Mail } from "lucide-react";

/**
 * Author bio card that lives at the bottom of every article — the reader has
 * finished the piece and now sees who wrote it, why they're worth reading, and
 * the follow buttons for social platforms.
 *
 * Props:
 *   author – { slug, name, role, bio, photo, twitter, instagram, threads, website, email }
 */
export default function AuthorBioCard({ author, articleAuthorName }) {
  if (!author && !articleAuthorName) return null;

  const name = author?.name || articleAuthorName;
  const role = author?.role || "Contributor";
  const bio = author?.bio || "Covering the Tennessee Titans for The Titan Standard.";
  const slug = author?.slug;

  const links = [];
  if (author?.twitter) links.push({ href: `https://twitter.com/${author.twitter.replace(/^@/, "")}`, label: `Follow on X`, icon: Twitter, testid: "authorbio-twitter" });
  if (author?.instagram) links.push({ href: `https://instagram.com/${author.instagram.replace(/^@/, "")}`, label: `Instagram`, icon: Instagram, testid: "authorbio-instagram" });
  if (author?.threads) links.push({ href: `https://www.threads.net/@${author.threads.replace(/^@/, "")}`, label: `Threads`, icon: AtSign, testid: "authorbio-threads" });
  if (author?.website) {
    const w = author.website.startsWith("http") ? author.website : `https://${author.website}`;
    links.push({ href: w, label: `Website`, icon: Globe, testid: "authorbio-website" });
  }
  if (author?.email) links.push({ href: `mailto:${author.email}`, label: `Email`, icon: Mail, testid: "authorbio-email" });

  return (
    <section
      data-testid="author-bio-card"
      className="mt-12 titans-card p-6 md:p-8 relative overflow-hidden border-2 border-titans-blue/25"
    >
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-titans-blue via-white to-titans-red" />
      <div className="font-cond uppercase tracking-[0.28em] text-[10px] text-titans-red mb-4">About the Author</div>
      <div className="flex flex-col sm:flex-row items-start gap-5">
        {author?.photo ? (
          <img
            src={author.photo}
            alt={name}
            className="w-24 h-24 md:w-28 md:h-28 object-cover shrink-0 border-2 border-titans-blue/40"
          />
        ) : (
          <div className="w-24 h-24 md:w-28 md:h-28 shrink-0 bg-titans-blue/15 border-2 border-titans-blue/40 flex items-center justify-center font-display text-titans-blue text-4xl">
            {(name || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          {slug ? (
            <Link to={`/author/${slug}`} className="font-display text-3xl md:text-4xl uppercase tracking-wide leading-tight hover:text-titans-red transition-colors" data-testid="authorbio-name-link">
              {name}
            </Link>
          ) : (
            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-wide leading-tight">{name}</h3>
          )}
          <div className="font-cond uppercase tracking-wider text-xs text-titans-blue mt-1">
            {role}
          </div>
          <p className="font-body mt-3 leading-relaxed max-w-2xl">{bio}</p>

          {(links.length > 0 || slug) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {links.map((l) => (
                <a
                  key={l.testid}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={l.testid}
                  className="border border-[rgba(15,23,42,0.15)] hover:border-titans-red hover:text-titans-red font-cond uppercase tracking-wider text-xs px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                >
                  <l.icon size={12} /> {l.label}
                </a>
              ))}
              {slug && (
                <Link
                  to={`/author/${slug}`}
                  data-testid="authorbio-subscribe"
                  className="bg-titans-red hover:bg-titans-red-hover text-white font-cond uppercase tracking-wider text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                >
                  <Mail size={12} /> Subscribe
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
